import { useEffect, useMemo, useState } from "react";

import { Dialog } from "../Dialog.jsx";
import { Icon } from "../Icon.jsx";
import { DoItemQuantityEditor } from "./DoItemQuantityEditor.jsx";

const PLAN_COPY = {
  READY_AUTO: {
    tone: "success",
    title: "Seluruh stok cukup",
    detail: "Urutan proses otomatis mengikuti nomor Sales Order.",
  },
  READY_WITH_SHORTAGE: {
    tone: "warning",
    title: "Sebagian dokumen belum dapat dialokasikan penuh",
    detail: "SO dengan stok tidak cukup tetap ditunda. DO sebagian hanya dibuat jika user mengatur Qty DO lebih kecil.",
  },
  REQUIRES_PRIORITY: {
    tone: "warning",
    title: "Ada rebutan stok",
    detail: "Tentukan prioritas SO yang menerima stok terlebih dahulu. Qty hanya berubah jika user mengeditnya.",
  },
  READY_SELECTED: {
    tone: "success",
    title: "Rencana sudah disimpan",
    detail: "Alokasi stok mengikuti urutan dan Qty DO yang sudah dikonfirmasi user.",
  },
  NO_ELIGIBLE_SOURCES: {
    tone: "neutral",
    title: "Belum ada dokumen yang dapat direncanakan",
    detail: "Selesaikan blocker dokumen terlebih dahulu.",
  },
};

const quantityFormatter = new Intl.NumberFormat("id-ID", {
  maximumFractionDigits: 6,
});

function formatQuantity(value) {
  return quantityFormatter.format(Number(value) || 0);
}

function formatTimestamp(value) {
  if (!value) return "Belum tersedia";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Belum tersedia";
  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

const EXECUTION_STATUS_LABELS = {
  QUEUED: "Menunggu",
  RUNNING: "Berjalan",
  PAUSE_REQUESTED: "Menunggu jeda",
  PAUSED_MANUAL: "Dijeda user",
  PAUSED_RECONCILIATION: "Perlu pemeriksaan",
  TERMINATE_REQUESTED: "Menghentikan",
  TERMINATED: "Dihentikan",
  COMPLETED: "Selesai",
  COMPLETED_WITH_SKIPS: "Selesai sebagian",
  FAILED: "Gagal",
};

const EXECUTION_STATUS_DETAILS = {
  QUEUED: "Antrean menunggu giliran pemrosesan.",
  RUNNING: "Sedang memproses Delivery Order satu per satu.",
  PAUSE_REQUESTED: "SO yang sedang aktif diselesaikan sebelum antrean dijeda.",
  PAUSED_MANUAL: "Antrean berhenti aman sebelum SO berikutnya. Lanjutkan saat siap.",
  PAUSED_RECONCILIATION: "Antrean dijeda karena satu hasil perlu diperiksa di Accurate.",
  TERMINATE_REQUESTED: "SO yang sedang aktif diselesaikan sebelum antrean dihentikan.",
  TERMINATED: "Antrean dihentikan. DO yang sudah berhasil tetap tersimpan.",
  COMPLETED: "Seluruh Delivery Order berhasil dibuat.",
  COMPLETED_WITH_SKIPS: "Antrean selesai dengan beberapa dokumen ditinggalkan.",
  FAILED: "Antrean berhenti karena gangguan sistem.",
};

function executionStatusLabel(status) {
  return EXECUTION_STATUS_LABELS[status] || status || "Belum dimulai";
}

function AllocationBadge({ mode, status }) {
  const allocated = status === "ALLOCATED";
  const partial = allocated && mode === "PARTIAL";
  return (
    <span
      className={`do-batch-plan-status ${
        partial
          ? "do-batch-plan-status--partial"
          : allocated
            ? "do-batch-plan-status--allocated"
            : "do-batch-plan-status--waiting"
      }`}
    >
      {partial ? "Diproses sebagian" : allocated ? "Dapat diproses penuh" : "Ditunda stok"}
    </span>
  );
}

function PlanDialog({
  busy,
  error,
  execution,
  onClose,
  onPauseExecution,
  onResumeExecution,
  onSavePriority,
  onStartExecution,
  onTerminateExecution,
  plan,
}) {
  const eligible = useMemo(
    () =>
      (plan?.sources || [])
        .filter((source) => source.eligibilityStatus === "ELIGIBLE")
        .sort((left, right) => left.priorityRank - right.priorityRank),
    [plan],
  );
  const blocked = useMemo(
    () => (plan?.sources || []).filter((source) => source.eligibilityStatus === "BLOCKED"),
    [plan],
  );
  const [orderKeys, setOrderKeys] = useState(() => eligible.map((source) => source.key));
  const [selectionBySource, setSelectionBySource] = useState(() =>
    Object.fromEntries(eligible.map((source) => [source.key, source.itemSelections || []])),
  );
  const [expandedKey, setExpandedKey] = useState(null);
  const [executionConfirmed, setExecutionConfirmed] = useState(false);

  useEffect(() => {
    setOrderKeys(eligible.map((source) => source.key));
    setSelectionBySource(
      Object.fromEntries(eligible.map((source) => [source.key, source.itemSelections || []])),
    );
    setExpandedKey(null);
    setExecutionConfirmed(false);
  }, [plan?.id, plan?.updatedAt]);

  const sourceByKey = useMemo(
    () => new Map(eligible.map((source) => [source.key, source])),
    [eligible],
  );
  const savedOrder = eligible.map((source) => source.key);
  const savedSelections = Object.fromEntries(
    eligible.map((source) => [source.key, source.itemSelections || []]),
  );
  const dirty =
    JSON.stringify(orderKeys) !== JSON.stringify(savedOrder) ||
    JSON.stringify(selectionBySource) !== JSON.stringify(savedSelections);
  const priorityRequired = Boolean(plan?.summary?.contestedItems);
  const executionActive = Boolean(execution?.active);
  const canPauseExecution = ["QUEUED", "RUNNING"].includes(execution?.status);
  const canResumeExecution = execution?.status === "PAUSED_MANUAL";
  const canTerminateExecution =
    Boolean(execution?.active) && execution?.status !== "TERMINATE_REQUESTED";
  const unresolvedEditors = eligible.filter((source) =>
    ["MANUAL_REQUIRED", "REVIEW_REQUIRED", "DATA_NOT_READY", "NO_QUANTITY"].includes(
      source.itemEditorStatus,
    ),
  ).length;
  const canStartExecution =
    plan.executionEnabled !== false &&
    plan.summary.selected > 0 &&
    (!priorityRequired || plan.priorityConfirmed) &&
    unresolvedEditors === 0 &&
    !dirty &&
    !executionActive;

  function move(key, direction) {
    setOrderKeys((current) => {
      const index = current.indexOf(key);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
    setExecutionConfirmed(false);
  }

  function updateSourceEditor(key, editor) {
    setSelectionBySource((current) => {
      const nextValue = editor.manualSelections || [];
      if (JSON.stringify(current[key] || []) === JSON.stringify(nextValue)) return current;
      return { ...current, [key]: nextValue };
    });
    setExecutionConfirmed(false);
  }

  const sourceItemSelections = orderKeys
    .map((sourceKey) => ({
      sourceKey,
      items: selectionBySource[sourceKey] || [],
    }))
    .filter((entry) => entry.items.length > 0);

  return (
    <Dialog
      className="do-batch-plan-dialog"
      onClose={busy ? () => {} : onClose}
      title="Rencana pembuatan Delivery Order"
    >
      <div className="do-batch-plan-dialog__body">
        <section className="do-batch-plan-dialog__intro">
          <div>
            <span>Urutan dan Qty Delivery Order</span>
            <h3>{plan.foNumber}</h3>
            <p>
              Sistem memperbarui stok seluruh gudang yang dipakai Fleet Order. Seluruh sisa SO digunakan saat stok cukup; DO sebagian hanya dibuat dari Qty yang diubah user.
            </p>
          </div>
          <div className="do-batch-plan-dialog__stock-time">
            <small>Stok terakhir diperbarui</small>
            <strong>{formatTimestamp(plan.stockCheckedAt)}</strong>
          </div>
        </section>

        <div className={`do-batch-plan-alert ${priorityRequired ? "do-batch-plan-alert--warning" : "do-batch-plan-alert--success"}`}>
          <Icon name={priorityRequired ? "warning" : "check"} size={18} />
          <div>
            <strong>{priorityRequired ? "Ada barang yang diperebutkan beberapa SO" : "Urutan otomatis berdasarkan nomor SO"}</strong>
            <span>
              {priorityRequired
                ? "Atur prioritas SO. Edit Qty hanya jika Anda sengaja membuat DO sebagian."
                : "Urutan otomatis dipakai dan Qty tetap penuh selama user tidak mengubahnya."}
            </span>
          </div>
        </div>

        {dirty && (
          <div className="do-batch-plan-alert do-batch-plan-alert--warning" role="status">
            <Icon name="warning" size={18} />
            <div>
              <strong>Perubahan belum dihitung ulang</strong>
              <span>Simpan rencana agar alokasi SO berikutnya mengikuti urutan dan Qty DO terbaru.</span>
            </div>
          </div>
        )}

        <div className="do-batch-plan-summary-grid do-batch-plan-summary-grid--phase2d">
          <div><span>DO penuh</span><strong>{Math.max(0, plan.summary.selected - (plan.summary.partialSelected || 0))}</strong></div>
          <div><span>DO sebagian</span><strong>{plan.summary.partialSelected || 0}</strong></div>
          <div><span>Qty perlu diatur</span><strong>{unresolvedEditors}</strong></div>
          <div><span>Blocker lain</span><strong>{blocked.length}</strong></div>
        </div>

        {execution && (
          <section className="do-batch-execution-list">
            <header>
              <div>
                <strong>Progres pemrosesan</strong>
                <span>Stok Accurate diperiksa kembali sebelum setiap SO ditulis.</span>
              </div>
              <span className={`do-batch-execution-state do-batch-execution-state--${execution.status.toLowerCase()}`}>
                {executionStatusLabel(execution.status)}
              </span>
            </header>
            <ol>
              {(execution.items || []).map((item) => (
                <li key={item.id}>
                  <span>{item.sequence}</span>
                  <div>
                    <strong>{item.sourceNumber}</strong>
                    <small>
                      {item.customerName || "Pelanggan belum tersedia"}
                      {item.executionMode === "PARTIAL" ? " · DO sebagian" : " · DO penuh"}
                    </small>
                    {item.errorMessage && <em>{item.errorMessage}</em>}
                  </div>
                  <b className={`do-batch-item-status do-batch-item-status--${item.status.toLowerCase()}`}>
                    {item.status === "PENDING" && "Menunggu"}
                    {item.status === "CHECKING_STOCK" && "Cek stok"}
                    {item.status === "CREATING" && "Membuat DO"}
                    {item.status === "CREATED" && (item.accurateDoNumber || "Berhasil")}
                    {item.status === "SKIPPED_STOCK" && "Stok berubah"}
                    {item.status === "SKIPPED_CHANGED" && "Data berubah"}
                    {item.status === "SKIPPED_ALREADY_CREATED" && "Sudah ada DO"}
                    {item.status === "FAILED" && "Gagal"}
                    {item.status === "RECONCILIATION_REQUIRED" && "Perlu diperiksa"}
                  </b>
                </li>
              ))}
            </ol>
          </section>
        )}

        <section className="do-batch-plan-priority-list" aria-label="Urutan prioritas Sales Order">
          <header>
            <div>
              <strong>Urutan dan Qty per Sales Order</strong>
              <span>Urutan dapat diubah saat stok diperebutkan. DO sebagian hanya terjadi dari Qty yang diedit user.</span>
            </div>
          </header>

          <ol>
            {orderKeys.map((key, index) => {
              const source = sourceByKey.get(key);
              if (!source) return null;
              const attention = ["MANUAL_REQUIRED", "REVIEW_REQUIRED", "DATA_NOT_READY", "NO_QUANTITY"].includes(
                source.itemEditorStatus,
              );
              return (
                <li className={attention ? "do-batch-plan-priority-list__item--attention" : source.executionMode === "PARTIAL" ? "do-batch-plan-priority-list__item--partial" : ""} key={key}>
                  <span className="do-batch-plan-priority-list__rank">{index + 1}</span>
                  <div className="do-batch-plan-priority-list__source">
                    <strong>{source.sourceNumber}</strong>
                    <span>{source.customerName || "Pelanggan belum tersedia"}</span>
                    <small>
                      {attention
                        ? "Ada Qty DO yang perlu diatur atau diperiksa."
                        : source.executionMode === "PARTIAL"
                          ? "Sebagian item masih tersisa setelah DO ini."
                          : "Seluruh sisa item dapat diproses."}
                    </small>
                    <button
                      className="do-batch-plan-priority-list__edit"
                      disabled={busy || executionActive}
                      onClick={() => setExpandedKey(expandedKey === key ? null : key)}
                      type="button"
                    >
                      {expandedKey === key ? "Tutup editor" : attention ? "Atur qty" : "Edit qty"}
                    </button>
                    {expandedKey === key && (
                      <div className="do-batch-plan-priority-list__editor">
                        <DoItemQuantityEditor
                          disabled={busy || executionActive}
                          initialSelections={selectionBySource[key] || []}
                          items={source.itemEditor?.length ? source.itemEditor : source.items || []}
                          onChange={(editor) => updateSourceEditor(key, editor)}
                          resetKey={`${plan.id}:${plan.updatedAt}:${key}`}
                        />
                      </div>
                    )}
                  </div>
                  <AllocationBadge mode={source.executionMode} status={source.allocationStatus} />
                  {priorityRequired && (
                    <div className="do-batch-plan-priority-list__actions">
                      <button
                        aria-label={`Naikkan prioritas ${source.sourceNumber}`}
                        disabled={busy || executionActive || index === 0}
                        onClick={() => move(key, -1)}
                        type="button"
                      >
                        Naik
                      </button>
                      <button
                        aria-label={`Turunkan prioritas ${source.sourceNumber}`}
                        disabled={busy || executionActive || index === orderKeys.length - 1}
                        onClick={() => move(key, 1)}
                        type="button"
                      >
                        Turun
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </section>

        {plan.stocks.some((stock) => stock.contested) && (
          <section className="do-batch-plan-stock-conflicts">
            <header>
              <strong>Barang yang membutuhkan prioritas</strong>
              <span>Stok dan kebutuhan menggunakan satuan terkecil.</span>
            </header>
            <div className="do-batch-plan-stock-conflicts__grid">
              {plan.stocks
                .filter((stock) => stock.contested)
                .map((stock) => (
                  <article key={stock.key}>
                    <strong>{stock.itemName}</strong>
                    <span>{stock.warehouseName}</span>
                    <dl>
                      <div><dt>Stok</dt><dd>{formatQuantity(stock.liveStock)}</dd></div>
                      <div><dt>Kebutuhan</dt><dd>{formatQuantity(stock.totalRequired)}</dd></div>
                      <div><dt>SO terkait</dt><dd>{stock.sourceNumbers.length}</dd></div>
                    </dl>
                  </article>
                ))}
            </div>
          </section>
        )}

        {blocked.length > 0 && (
          <details className="do-batch-plan-blocked">
            <summary>{blocked.length} dokumen tidak masuk rencana</summary>
            <ul>
              {blocked.map((source) => (
                <li key={source.key}>
                  <strong>{source.sourceNumber}</strong>
                  <span>{source.blockReason || "Dokumen belum siap."}</span>
                </li>
              ))}
            </ul>
          </details>
        )}

        {error && <div className="error-banner">{error}</div>}
      </div>

      <footer className="dialog-card__actions do-batch-plan-dialog__actions do-batch-plan-dialog__actions--phase2b">
        <div className="do-batch-plan-execution-confirmation">
          {executionActive ? (
            <span>{EXECUTION_STATUS_DETAILS[execution?.status] || "Progres tetap tersimpan walaupun modal ditutup."}</span>
          ) : (
            <label>
              <input
                checked={executionConfirmed}
                disabled={busy || !canStartExecution}
                onChange={(event) => setExecutionConfirmed(event.target.checked)}
                type="checkbox"
              />
              <span>
                Saya sudah memeriksa urutan dan Qty DO. Sistem tidak akan mengurangi Qty otomatis bila stok berubah.
              </span>
            </label>
          )}
        </div>
        <div className="do-batch-plan-dialog__button-group">
          <button className="secondary-button" disabled={busy} onClick={onClose} type="button">
            Tutup
          </button>
          {executionActive ? (
            <>
              {canPauseExecution && (
                <button className="secondary-button" disabled={busy} onClick={onPauseExecution} type="button">
                  {busy ? "Memproses…" : "Jeda setelah SO aktif"}
                </button>
              )}
              {canResumeExecution && (
                <button className="primary-button" disabled={busy} onClick={onResumeExecution} type="button">
                  {busy ? "Melanjutkan…" : "Lanjutkan antrean"}
                </button>
              )}
              {canTerminateExecution && (
                <button className="danger-button" disabled={busy} onClick={onTerminateExecution} type="button">
                  {busy ? "Memproses…" : "Hentikan antrean"}
                </button>
              )}
            </>
          ) : (
            <>
              <button
                className="secondary-button"
                disabled={busy || (!dirty && (!priorityRequired || plan.priorityConfirmed))}
                onClick={() => onSavePriority(orderKeys, sourceItemSelections)}
                type="button"
              >
                {busy ? "Menyimpan…" : "Simpan & hitung ulang"}
              </button>
              <button
                className="primary-button"
                disabled={busy || !canStartExecution || !executionConfirmed}
                onClick={onStartExecution}
                type="button"
              >
                {busy
                  ? "Memulai antrean…"
                  : `Proses ${plan.summary.selected} SO berurutan`}
              </button>
            </>
          )}
        </div>
      </footer>
    </Dialog>
  );
}

export function DoBatchPlanPanel({
  busy,
  error,
  execution,
  onCreate,
  onOpen,
  onPauseExecution,
  onResumeExecution,
  onSavePriority,
  onStartExecution,
  onTerminateExecution,
  plan,
  progressStage = "",
  warehouseName = "gudang Fleet Order",
}) {
  const copy = PLAN_COPY[plan?.status] || null;
  const executionActive = Boolean(execution?.active);
  const completedCount = execution
    ? execution.summary.created + execution.summary.skippedStock +
      execution.summary.skippedChanged + execution.summary.skippedExisting +
      execution.summary.failed + execution.summary.reconciliation
    : 0;

  return (
    <>
      <section className={`do-batch-plan-card ${copy ? `do-batch-plan-card--${copy.tone}` : ""}`}>
        <div className="do-batch-plan-card__icon" aria-hidden="true">
          <Icon name={copy?.tone === "warning" ? "warning" : "partial"} size={20} />
        </div>
        <div className="do-batch-plan-card__copy">
          <span>Rencana Pembuatan Delivery Order</span>
          <strong>{copy?.title || "Susun rencana berdasarkan stok terbaru"}</strong>
          <p>
            {copy?.detail ||
              "Sistem memperbarui stok gudang, mengalokasikan stok per Sales Order, lalu memprosesnya satu per satu."}
          </p>
          {progressStage && (
            <div className="do-batch-plan-card__stage" role="status">
              <Icon name="sync" size={15} />
              <span>
                <strong>
                  {progressStage === "STOCK"
                    ? `Memperbarui stok ${warehouseName}`
                    : "Menghitung alokasi stok terbaru"}
                </strong>
                <small>
                  {progressStage === "STOCK"
                    ? "Hanya gudang yang digunakan Fleet Order ini yang diperbarui."
                    : "Sistem memakai hasil sinkronisasi terbaru untuk seluruh gudang efektif dalam Fleet Order."}
                </small>
              </span>
            </div>
          )}
          {execution ? (
            <div className="do-batch-execution-progress" aria-live="polite">
              <div>
                <span>Progres antrean</span>
                <strong>{completedCount}/{execution.summary.total}</strong>
              </div>
              <progress max={Math.max(1, execution.summary.total)} value={completedCount} />
              <small>
                {EXECUTION_STATUS_DETAILS[execution.status] || "Status antrean diperbarui."}
                {execution.summary.partialCreated > 0
                  ? ` · ${execution.summary.partialCreated} DO sebagian berhasil.`
                  : ""}
              </small>
            </div>
          ) : plan && (
            <small>
              {plan.summary.selected} dipilih · {plan.summary.partialSelected || 0} sebagian · {plan.summary.waitingStock} ditunda · diperiksa {formatTimestamp(plan.stockCheckedAt)}
            </small>
          )}
        </div>
        <div className="do-batch-plan-card__actions">
          {plan && (
            <button className="secondary-button" disabled={busy} onClick={onOpen} type="button">
              {execution ? "Lihat progres & kontrol" : plan.status === "REQUIRES_PRIORITY" ? "Atur rencana" : "Lihat rencana"}
            </button>
          )}
          {execution?.status === "PAUSED_MANUAL" && (
            <button className="primary-button" disabled={busy} onClick={onResumeExecution} type="button">
              Lanjutkan antrean
            </button>
          )}
          {["QUEUED", "RUNNING"].includes(execution?.status) && (
            <button className="secondary-button" disabled={busy} onClick={onPauseExecution} type="button">
              Jeda
            </button>
          )}
          {executionActive && execution?.status !== "TERMINATE_REQUESTED" && (
            <button className="danger-button" disabled={busy} onClick={onTerminateExecution} type="button">
              Hentikan
            </button>
          )}
          <button
            className="primary-button"
            disabled={busy || executionActive}
            onClick={onCreate}
            type="button"
          >
            <Icon name="refresh" size={16} />
            {progressStage === "STOCK"
              ? `Memperbarui ${warehouseName}…`
              : progressStage === "PLANNING"
                ? "Menghitung alokasi…"
                : busy
                  ? "Memproses…"
              : executionActive
                ? "Antrean sedang berjalan"
                : plan
                  ? "Periksa ulang & susun rencana"
                  : "Perbarui stok & susun rencana"}
          </button>
        </div>
      </section>
      {error && <div className="do-batch-plan-card__error" role="alert">{error}</div>}
    </>
  );
}

export function DoBatchPlanDialog(props) {
  return <PlanDialog {...props} />;
}
